import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Clock, TrendingUp, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../utils/currencyUtils';
import type { ExchangeRates } from '../utils/currencyUtils';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ROITimelineChartProps {
  purchasePrice: number;
  monthlyProfit: number;
  writeoffMonths: number;
  currency: string;
  exchangeRates: ExchangeRates;
}

export function ROITimelineChart({
  purchasePrice,
  monthlyProfit,
  writeoffMonths,
  currency,
  exchangeRates
}: ROITimelineChartProps) {
  const generateTimelineData = () => {
    const data = [];
    let cumulativeProfit = -purchasePrice;
    
    for (let month = 0; month <= writeoffMonths; month++) {
      data.push({
        month,
        cumulativeProfit,
        monthlyProfit,
        investment: -purchasePrice,
        isProfitable: monthlyProfit > 0
      });
      cumulativeProfit += monthlyProfit;
    }
    
    return data;
  };

  const timelineData = generateTimelineData();
  const breakEvenMonth = timelineData.findIndex(d => d.cumulativeProfit >= 0);
  const finalProfit = timelineData[timelineData.length - 1].cumulativeProfit;
  const isProfitable = monthlyProfit > 0;

  const chartData = {
    labels: timelineData.map(d => `Month ${d.month}`),
    datasets: [
      {
        label: 'Cumulative Profit',
        data: timelineData.map(d => d.cumulativeProfit),
        borderColor: isProfitable ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)',
        backgroundColor: (context: any) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;
          
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
          if (isProfitable) {
            gradient.addColorStop(0, 'rgba(59, 130, 246, 0.1)');
            gradient.addColorStop(1, 'rgba(59, 130, 246, 0.3)');
          } else {
            gradient.addColorStop(0, 'rgba(239, 68, 68, 0.1)');
            gradient.addColorStop(1, 'rgba(239, 68, 68, 0.3)');
          }
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: (context: any) => {
          return context.dataIndex === breakEvenMonth ? 8 : 0;
        },
        pointBackgroundColor: (context: any) => {
          return context.dataIndex === breakEvenMonth ? 'rgb(34, 197, 94)' : 
                 isProfitable ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)';
        },
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointHoverRadius: 8
      },
      {
        label: 'Initial Investment',
        data: timelineData.map(d => d.investment),
        borderColor: 'rgb(100, 116, 139)',
        borderDash: [5, 5],
        pointRadius: 0
      },
      {
        label: 'Break-even Line',
        data: timelineData.map(() => 0),
        borderColor: 'rgb(34, 197, 94)',
        borderDash: [2, 2],
        pointRadius: 0
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#1f2937',
        bodyColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: (context: any) => {
            const value = context.raw as number;
            const label = context.dataset.label;
            if (context.dataIndex === breakEvenMonth && label === 'Cumulative Profit') {
              return `Break-even Point: ${formatCurrency(value, currency, exchangeRates)}`;
            }
            return `${label}: ${formatCurrency(value, currency, exchangeRates)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 6
        }
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          callback: (value: any) => formatCurrency(value, currency, exchangeRates)
        }
      }
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-500" />
          ROI Timeline
        </h2>
      </div>

      {!isProfitable && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">
            Warning: With current electricity costs, this miner is not profitable. 
            Monthly loss: {formatCurrency(Math.abs(monthlyProfit), currency, exchangeRates)}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className={`rounded-lg p-4 ${isProfitable ? 'bg-blue-50' : 'bg-red-50'}`}>
          <div className="flex items-center gap-2 mb-1">
            <Clock className={`w-4 h-4 ${isProfitable ? 'text-blue-500' : 'text-red-500'}`} />
            <p className="text-sm text-gray-600">Monthly Profit/Loss</p>
          </div>
          <p className={`text-lg font-semibold ${isProfitable ? 'text-blue-600' : 'text-red-600'}`}>
            {formatCurrency(monthlyProfit, currency, exchangeRates)}
          </p>
        </div>

        <div className={`rounded-lg p-4 ${breakEvenMonth >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className={`w-4 h-4 ${breakEvenMonth >= 0 ? 'text-green-500' : 'text-red-500'}`} />
            <p className="text-sm text-gray-600">Break Even Point</p>
          </div>
          <p className={`text-lg font-semibold ${breakEvenMonth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {breakEvenMonth >= 0 ? `Month ${breakEvenMonth}` : 'Never'}
          </p>
        </div>
      </div>

      <div className="h-[400px]">
        <Line data={chartData} options={options} />
      </div>

      <div className="mt-6 p-4 rounded-lg bg-gray-50">
        <p className="text-sm text-gray-600">
          {isProfitable ? (
            breakEvenMonth >= 0 ? (
              `Break-even achieved after ${breakEvenMonth} months. Total return after ${writeoffMonths} months: ${
                formatCurrency(finalProfit, currency, exchangeRates)
              }`
            ) : (
              `Investment will be profitable but won't reach break-even within ${writeoffMonths} months`
            )
          ) : (
            'Investment will not be profitable with current electricity costs'
          )}
        </p>
      </div>
    </div>
  );
}